import { BaseAdapter } from './BaseAdapter'
import { BackendProvider } from './types'
import {
	PlaceOrModifyOrdersRequest,
	PlaceOrModifyOrdersResponse,
	GetAllOrdersRequest,
	CancelOrdersRequest,
	CancelOrdersResponse
} from '../proto/generated/trading-bridge'
import { Order, OrderStatus } from '../proto/generated/order'
import { PositionRequest, PositionResponse } from '../proto/generated/position'
import {
	AuthProviderLoginUrlResponse,
	ForgetPasswordRequest,
	GetActiveSessionsResponse,
	LoginRequest,
	LoginResponse,
	OperationResponse,
	User,
	UserSignUpRequest,
	ValidateThirdPartyTokenRequest
} from '../proto/generated/identity'
import { clearAuthSession } from '../services/auth/tokenRefresh'

export class ShoonyaAdapter extends BaseAdapter {
	transformRequest<T = any>(
		endpoint: string,
		data: T,
		method: string
	): {
		url: string
		data: any
		headers?: Record<string, string>
	} {
		const mappedEndpoint = this.mapEndpoint(endpoint)

		const transformedData = this.transformRequestData(data, endpoint)

		return {
			url: mappedEndpoint,
			data: transformedData,
			headers: {
				...this.getCustomHeaders(),
				'X-API-Key': this.config.apiKey || '',
				'X-Source': 'tick2trade'
			}
		}
	}

	transformResponse<T = any>(response: any, endpoint: string): T {
		return this.transformResponseData(response, endpoint) as T
	}

	private mapEndpoint(endpoint: string): string {
		const endpointMap: Record<string, string> = {
			// Securities
			'/securities/lookup': '/api/v1/securities/search',
			'/securities/overview': '/api/v1/securities/details',
			'/securities/scan': '/api/v1/securities/scan',
			'/securities/categories': '/api/v1/securities/categories',

			// Options
			'/options/optionchain': '/api/v1/options/chain',
			'/options/expiries': '/api/v1/options/expiry-dates',

			// Futures
			'/futures': '/api/v1/futures/list',

			// Watchlist
			'/watchlists': '/api/v1/watchlists',

			// Trading accounts
			'/trading-accounts': '/api/v1/accounts',
			'/trading-accounts/margins': '/api/v1/accounts/margins',
			'/trading-accounts/login-url': '/api/v1/accounts/auth-url',
			'/trading-accounts/connect': '/api/v1/accounts/connect',

			// Orders & Positions
			'/orders': '/api/v1/orders',
			'/positions': '/api/v1/positions',

			// Market data
			'/tickdata/security-snapshot': '/api/v1/market/snapshot',
			'/tickdata/order-book': '/api/v1/market/depth',
			'/tickdata/order-books': '/api/v1/market/depths',

			// Alerts
			'/alerts': '/api/v1/alerts',
			'/alerts/all': '/api/v1/alerts/list',
			'/alerts/fired/all': '/api/v1/alerts/triggered',

			// Screener
			'/screener/meta/global-screener-columns': '/api/v1/screener/columns',
			'/screener/meta/global-column-sets': '/api/v1/screener/column-sets',
			'/screener/meta/screener-fields': '/api/v1/screener/fields',

			// News
			'/marketnews': '/api/v1/news/market',

			// Indicators
			'/indicators/script': '/api/v1/indicators/script',

			// Auth
			'/auth/login-url': '/api/v1/auth/login-url',
			'/auth/oauth/token/validate': '/api/v1/auth/validate',
		}

		if (endpointMap[endpoint]) {
			return endpointMap[endpoint]
		}

		for (const [key, value] of Object.entries(endpointMap)) {
			if (endpoint.startsWith(key)) {
				return endpoint.replace(key, value)
			}
		}

		console.warn(`No endpoint mapping found for: ${endpoint}`)
		return endpoint
	}

	private transformRequestData(data: any, endpoint: string): any {
		if (!data) return data

		if (endpoint.includes('/securities/lookup')) {
			return this.transformSecurityLookupRequest(data)
		}

		if (endpoint.includes('/options/optionchain')) {
			return this.transformOptionChainRequest(data)
		}

		if (endpoint.includes('/watchlists')) {
			return this.transformWatchlistRequest(data)
		}

		if (endpoint.includes('/orders')) {
			return this.transformOrderRequest(data)
		}

		// Default: return data as-is
		return data
	}

	private transformResponseData(response: any, endpoint: string): any {
		if (!response) return response

		if (endpoint.includes('/securities/lookup')) {
			return this.transformSecurityLookupResponse(response)
		}

		if (endpoint.includes('/options/optionchain')) {
			return this.transformOptionChainResponse(response)
		}

		if (endpoint.includes('/watchlists')) {
			return this.transformWatchlistResponse(response)
		}

		if (endpoint.includes('/orders')) {
			return this.transformOrderResponse(response)
		}

		return response
	}

	private transformSecurityLookupRequest(data: any): any {
		return {
			query: data.query || data.searchText,
			exchange: data.exchange,
			limit: data.limit || 10,
			filters: data.filters
		}
	}

	private transformOptionChainRequest(data: any): any {
		return {
			symbol: data.securityCode || data.symbol,
			expiry: data.expiry,
			exchange: data.exchange,
			strikeCount: data.strikeCount
		}
	}

	private transformWatchlistRequest(data: any): any {
		return {
			name: data.name,
			securities: data.securities?.map((sec: any) => ({
				symbol: sec.code || sec.symbol,
				exchange: sec.exchange
			}))
		}
	}

	private transformOrderRequest(data: any): any {
		return {
			symbol: data.securityCode || data.symbol,
			quantity: data.quantity,
			price: data.price,
			orderType: data.orderType,
			side: data.transactionType,
			productType: data.productType,
			validity: data.validity
		}
	}

	private transformSecurityLookupResponse(response: any): any {
		if (!response?.data) return response

		return {
			securities: response.data.map((item: any) => ({
				code: item.symbol || item.code,
				symbol: item.symbol,
				displayName: item.name || item.displayName,
				exchange: item.exchange,
				series: item.series,
				categoryCode: item.category || item.categoryCode,
				logoUrl: item.logo || item.logoUrl
			}))
		}
	}

	private transformOptionChainResponse(response: any): any {
		if (!response?.data) return response

		return {
			strikes: response.data.strikes?.map((strike: any) => ({
				strikePrice: strike.strike || strike.strikePrice,
				call: {
					ltp: strike.call?.lastPrice || strike.call?.ltp,
					volume: strike.call?.volume,
					oi: strike.call?.openInterest || strike.call?.oi,
					iv: strike.call?.impliedVolatility || strike.call?.iv,
					delta: strike.call?.greeks?.delta || strike.call?.delta,
					gamma: strike.call?.greeks?.gamma || strike.call?.gamma,
					theta: strike.call?.greeks?.theta || strike.call?.theta,
					vega: strike.call?.greeks?.vega || strike.call?.vega
				},
				put: {
					ltp: strike.put?.lastPrice || strike.put?.ltp,
					volume: strike.put?.volume,
					oi: strike.put?.openInterest || strike.put?.oi,
					iv: strike.put?.impliedVolatility || strike.put?.iv,
					delta: strike.put?.greeks?.delta || strike.put?.delta,
					gamma: strike.put?.greeks?.gamma || strike.put?.gamma,
					theta: strike.put?.greeks?.theta || strike.put?.theta,
					vega: strike.put?.greeks?.vega || strike.put?.vega
				}
			})),
			underlyingPrice: response.data.spotPrice || response.data.underlyingPrice,
			expiry: response.data.expiry || response.data.expiryDate
		}
	}

	private transformWatchlistResponse(response: any): any {
		if (!response?.data) return response

		return {
			watchlists: response.data.map((wl: any) => ({
				id: wl.id || wl.watchlistId,
				name: wl.name,
				securities: wl.securities?.map((sec: any) => ({
					code: sec.symbol || sec.code,
					exchange: sec.exchange,
					displayName: sec.name || sec.displayName
				}))
			}))
		}
	}

	private transformOrderResponse(response: any): any {
		if (!response?.data) return response

		return {
			orderId: response.data.id || response.data.orderId,
			status: response.data.status,
			message: response.data.message,
			orderDetails: {
				symbol: response.data.symbol,
				quantity: response.data.quantity,
				price: response.data.price,
				filledQuantity: response.data.filled || response.data.filledQuantity,
				averagePrice: response.data.avgPrice || response.data.averagePrice
			}
		}
	}

	transformError(error: any): any {
		const baseError = super.transformError(error)
		return {
			...baseError,
			provider: BackendProvider.SHOONYA,
			originalError: error
		}
	}

	async placeOrModifyOrder(request: PlaceOrModifyOrdersRequest): Promise<PlaceOrModifyOrdersResponse> {
		return {
			orderResponse: request.order.map((order, index) => ({
				orderId: `MOCK_ORDER_${Date.now()}_${index}`,
				successful: true,
				errorMessage: ''
			}))
		} as PlaceOrModifyOrdersResponse
	}

	async getOrders(request: GetAllOrdersRequest): Promise<Order[]> {
		return []
	}

	async cancelOrder(request: CancelOrdersRequest): Promise<CancelOrdersResponse> {
		return {
			orderResponse: request.orderId.map(orderId => ({
				orderId: orderId,
				successful: true,
				errorMessage: ''
			}))
		} as CancelOrdersResponse
	}

	async getPositions(request: PositionRequest): Promise<PositionResponse> {
		return {
			position: []
		} as PositionResponse
	}

	async getPlacedOrder(params: any): Promise<Order> {
		return {
			orderId: params.orderId || 'MOCK_ORDER',
			status: OrderStatus.PENDING
		} as Order
	}

	async getLoginUrl(body: any): Promise<AuthProviderLoginUrlResponse> {
		return {
			authProvider: body.authProvider || 'MOCK_PROVIDER',
			url: 'https://mock-shoonya-login.com/auth',
			redirectParamsKey: ''
		} as unknown as AuthProviderLoginUrlResponse
	}

	async loginWithMail(body: LoginRequest): Promise<LoginResponse> {
		return {
			accessToken: 'MOCK_TOKEN_' + Date.now(),
			tokenType: 'Bearer',
			user: {
				id: 'MOCK_USER_ID',
				email: body.email,
				name: 'Mock User'
			}
		} as LoginResponse
	}

	async signUpWithMail(userSignUpRequest: UserSignUpRequest): Promise<LoginResponse> {
		return {
			accessToken: 'MOCK_TOKEN_' + Date.now(),
			tokenType: 'Bearer',
			isNewUser: false
		} as LoginResponse
	}

	async sendEmailOtp(email: string, _options?: { purpose?: string }): Promise<any> {
		return {
			data: {
				success: true,
				message: 'OTP sent successfully'
			}
		}
	}

	async forgetPassword(body: ForgetPasswordRequest): Promise<User> {
		return {
			id: 'MOCK_USER_ID',
			email: body.email,
			name: 'Mock User',
			avatarUrl: '',
			provider: 'EMAIL',
			providerUserId: '',
			emailVerified: true,
			phoneVerified: false,
			createdAt: Date.now(),
			updatedAt: Date.now(),
			status: 'ACTIVE',
			countryCode: '',
			mobile: '',
			mobileVerified: false
		} as unknown as User
	}

	async validateToken(body: ValidateThirdPartyTokenRequest): Promise<LoginResponse> {
		return {
			accessToken: 'MOCK_TOKEN_' + Date.now(),
			tokenType: 'Bearer',
			isNewUser: false
		}
	}

	logout(): void {
		clearAuthSession()
	}

	async getActiveSessions(): Promise<GetActiveSessionsResponse> {
		return { sessions: [] }
	}

	async revokeSession(_deviceId: string): Promise<OperationResponse> {
		return { success: true, message: '' }
	}
}
